// FAZ-9 / MOD-18 — `groupRoleMapJson` (BE ham JSON) ile ekrandaki satır bazlı
// grup→rol editör modeli arasında iki yönlü dönüşüm.
//
// BE her iki değer biçimini de kabul eder: `{"grupCN":"Rol"}` (tek rol) ve
// `{"grupCN":["Rol1","Rol2"]}` (çok rol). Serialize tarafında tek rol → string,
// çok rol → dizi olarak yazılır (kompakt + BE örnekleriyle tutarlı). Parse tarafı
// bozuk/eksik girdiye karşı savunmacıdır (kırılmaz, boş liste döner).

/** Ekrandaki bir grup→rol eşleme satırı. `id` yalnız React liste anahtarıdır. */
export interface GroupRoleMapping {
  id: string;
  group: string;
  roles: string[];
}

function newId(): string {
  // jsdom + modern tarayıcılarda mevcut; olmayan ortamda zaman tabanlı fallback.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `grm_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function toRoles(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .filter((r): r is string => typeof r === 'string')
    .map((r) => r.trim())
    .filter((r) => r.length > 0);
}

/** Ham JSON string'i satır listesine çözer. Bozuk/boş → `[]`. */
export function parseGroupRoleMap(json: string): GroupRoleMapping[] {
  const trimmed = json.trim();
  if (!trimmed) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return [];
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return [];
  }

  return Object.entries(parsed as Record<string, unknown>).map(([group, value]) => ({
    id: newId(),
    group,
    roles: toRoles(value),
  }));
}

/**
 * Satır listesini ham JSON string'e serialize eder. Boş grup adı veya rolsüz
 * satırlar atlanır; grup adları trim'lenir; roller sıra korunarak tekilleştirilir.
 * Tek rol → string, çok rol → dizi. Boş liste → `"{}"`.
 */
export function serializeGroupRoleMap(rows: GroupRoleMapping[]): string {
  const obj: Record<string, string | string[]> = {};
  for (const row of rows) {
    const group = row.group.trim();
    const roles = [...new Set(row.roles.map((r) => r.trim()).filter(Boolean))];
    if (!group || roles.length === 0) continue;
    // Tek rol → string (join tek elemanı verir, index-erişim undefine'ı olmadan).
    obj[group] = roles.length === 1 ? roles.join('') : roles;
  }
  return JSON.stringify(obj);
}
